import isNil from 'lodash/isNil'
import { SYNTHETIC_ORG_ID } from '@omnea-app/packages/universal/e2e-test-constants/syntheticOrg'
import type { Context, SegmentEvent } from '@segment/analytics-next'
import type { MiddlewareParams } from '@segment/analytics-next/dist/types/plugins/middleware'

export type Track = (event: string | SegmentEvent, properties?: any) => Promise<Context>
type Page = (event: string, properties?: any) => Promise<Context>
type Identify = (userId: string, properties?: any) => Promise<Context>
type Group = (orgId: string, properties?: any) => Promise<Context>
type Analytics = {
  track: Track
  page: Page
  identify: Identify
  group: Group
}

const getStubbedAnalytics = async (): Promise<Analytics> => {
  const track: Track = (event, properties) => {
    console.debug('stubbed analytics TRACK:', event, properties)
    return {} as Promise<Context>
  }
  const page: Page = (name, properties) => {
    console.debug('stubbed analytics PAGE:', name, properties)
    return {} as Promise<Context>
  }
  const identify: Identify = (userId, properties) => {
    console.debug('stubbed analytics IDENTIFY:', userId, properties)
    return {} as Promise<Context>
  }
  const group: Group = (orgId, properties) => {
    console.debug('stubbed analytics GROUP:', orgId, properties)
    return {} as Promise<Context>
  }

  return { track, page, identify, group }
}

const dropPingdomEvents = ({ payload, next }: MiddlewareParams) => {
  if (payload.userAgent() === 'Pingdom') {
    return
  }
  next(payload)
}

// The synthetic verification org runs a browser monitor against production; drop all of its
// events at the source so they never reach Segment. Only identify/group carry the org id, so the
// decision is updated only when it is present — a later org-less identify (e.g. flag enrichment)
// leaves it intact. track/page carry no org and follow the last resolved decision.
const dropSyntheticOrgEvents = () => {
  let isSyntheticOrg = false
  return ({ payload, next }: MiddlewareParams) => {
    try {
      const { type, groupId, traits } = payload.obj
      if (type === 'group' && groupId != null) {
        isSyntheticOrg = groupId === SYNTHETIC_ORG_ID
      } else if (type === 'identify' && traits?.organisationId != null) {
        isSyntheticOrg = traits.organisationId === SYNTHETIC_ORG_ID
      }
    } catch {
      // A malformed payload must never break the analytics pipeline or affect other orgs: leave
      // the last decision untouched and fall through to sending this event.
    }
    if (isSyntheticOrg) {
      return
    }
    next(payload)
  }
}

// Query params that hold customer free text. Segment copies url/search/referrer from window.location
// onto every event, not just page views, so they are removed on every route.
// q, qf: document search question and chosen suggestion.
const PRIVATE_QUERY_PARAMS = ['q', 'qf']

const PAGE_URL_FIELDS = ['url', 'search', 'referrer'] as const

const removePrivateQueryParams = (value: string) => {
  const queryStart = value.indexOf('?')
  if (queryStart === -1) return value
  const hashStart = value.indexOf('#', queryStart)
  const queryEnd = hashStart === -1 ? value.length : hashStart
  const params = new URLSearchParams(value.slice(queryStart + 1, queryEnd))
  if (!PRIVATE_QUERY_PARAMS.some((param) => params.has(param))) return value
  PRIVATE_QUERY_PARAMS.forEach((param) => params.delete(param))
  const kept = params.toString()
  return `${value.slice(0, queryStart)}${kept ? `?${kept}` : ''}${value.slice(queryEnd)}`
}

export const stripPrivateQueryParams = ({ properties, context }: SegmentEvent) => {
  for (const fields of [properties, context?.page]) {
    if (!fields) continue
    for (const field of PAGE_URL_FIELDS) {
      const value = fields[field]
      if (typeof value === 'string') fields[field] = removePrivateQueryParams(value)
    }
  }
}

const dropPrivateQueryParams = ({ payload, next }: MiddlewareParams) => {
  stripPrivateQueryParams(payload.obj)
  next(payload)
}

type GetSegmentAnalyticsOptions = {
  segmentWriteKey: string
  cdnUrl?: string
  apiHost?: string
}

const getSegmentAnalytics = async ({ segmentWriteKey, cdnUrl, apiHost }: GetSegmentAnalyticsOptions): Promise<Analytics> => {
  // Must stay off module scope: a static import evaluates the whole Segment bundle during SSR,
  // which never reaches this function.
  const { AnalyticsBrowser } = await import('@segment/analytics-next')

  // https://www.twilio.com/docs/segment/connections/sources/catalog/libraries/website/javascript/custom-proxy#npm-instructions
  const analytics = AnalyticsBrowser.load(
    {
      writeKey: segmentWriteKey,
      cdnURL: cdnUrl,
    },
    isNil(apiHost)
      ? undefined
      : {
          integrations: {
            'Segment.io': {
              // POST https://MY-CUSTOM-API-PROXY.com/v1/t --> proxies to
              // https://api.segment.io/v1/t
              apiHost: `${apiHost}/v1`,
              protocol: 'https',
            },
          },
        }
  )

  await analytics.addSourceMiddleware(dropPingdomEvents)
  await analytics.addSourceMiddleware(dropSyntheticOrgEvents())
  await analytics.addSourceMiddleware(dropPrivateQueryParams)

  return {
    track: analytics.track,
    page: analytics.page,
    identify: analytics.identify,
    group: analytics.group,
  }
}

export const getAnalyticsClient = async (options: GetSegmentAnalyticsOptions): Promise<Analytics> => {
  try {
    // only run in browser
    return typeof window !== 'undefined' && process.env.OMNEA_APP_ENV === 'production'
      ? await getSegmentAnalytics(options)
      : getStubbedAnalytics()
  } catch (err) {
    console.error('Error loading analytics client', err)
    return await getStubbedAnalytics()
  }
}

export const analytics = getAnalyticsClient({
  segmentWriteKey: process.env.SEGMENT_WRITE_KEY || '',
  cdnUrl: process.env.SEGMENT_CDN_URL,
  apiHost: process.env.SEGMENT_API_HOST,
})
